import React, { useEffect, useState, useRef } from "react";
import { useLive } from "@/lib/live";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import MyTasksBanner from "@/components/MyTasksBanner";
import {
  Truck, Package, Phone, QrCode, Eye, Camera,
  CheckCircle, SignOut, ArrowClockwise,
} from "@phosphor-icons/react";
import { istToday } from "@/lib/istDate";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

/**
 * DeliveryPanel — simplified flow requested 2026-08-17:
 * Delivery boy sees only 2 buttons per card: DESIGN and DELIVER.
 *   • DESIGN → opens design preview modal (same panel, big close ✕).
 *   • DELIVER → if balance is 0, one confirm and the order flips to
 *     DELIVERED; if balance > 0 we auto-generate the UPI QR (using the
 *     admin-configured default customer payment mode) and show a TAKE PHOTO
 *     button. Uploading the payment screenshot marks the order delivered —
 *     amount + mode come from the QR headers, no manual entry.
 */
export default function DeliveryPanel({ embedded = false }) {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [designPreview, setDesignPreview] = useState(null); // { url, filename }
  const [deliverModal, setDeliverModal] = useState(null);   // { order, qr?, upi_id?, amount?, mode_key?, holder? }
  const [tab, setTab] = useState("ready");                  // 'ready' | 'today'
  const [today, setToday] = useState([]);                    // delivered today list
  const [successOrder, setSuccessOrder] = useState(null);    // { order_number } — after upload

  useLive(() => { loadToday(); load(); });
  const loadToday = async () => {
    try {
      const { data } = await api.get("/delivery/dispatched");
      // Filter to just today's local date so the "today" tab is clean.
      const todayIso = istToday();
      setToday((data || []).filter((o) => (o.delivered_at || "").slice(0, 10) === todayIso));
    } catch {}
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/delivery/queue");
      setOrders(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); loadToday(); }, []);

  const previewDesign = async (order) => {
    const img = order.design_preview || order.design_pdf || (order.reference_images || [])[0];
    if (!img || !img.path) { toast.info("No design preview uploaded for this order."); return; }
    try {
      const kind = img === order.design_preview ? "design_preview" :
                    img === order.design_pdf ? "design_pdf" : "reference_image";
      const resp = await api.get(`/orders/${order.id}/download/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      setDesignPreview({ url, filename: img.original_filename || "design" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Design load failed");
    }
  };

  const openDeliver = async (order) => {
    const due = Math.max(Number(order.total_amount || 0) - Number(order.advance_paid || 0) - Number(order.discount || 0), 0);
    if (due <= 0) {
      // Nothing to collect — one-tap flip to delivered.
      if (!window.confirm(`Pura payment ho chuka hai. #${order.order_number} ko DELIVERED mark karein?`)) return;
      try {
        await api.post(`/delivery/${order.id}/complete`, { amount: 0, mode: "cash", note: "Fully paid — direct delivery" });
        toast.success(`#${order.order_number} DELIVERED ✓`);
        load();
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Delivery failed");
      }
      return;
    }
    // Balance pending — fetch QR
    try {
      const resp = await api.get(`/delivery/qr?order_id=${order.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      setDeliverModal({
        order,
        qr: url,
        upi_id: resp.headers["x-upi-id"] || "",
        holder: resp.headers["x-upi-holder"] || "",
        amount: Number(resp.headers["x-amount"] || due),
        mode_key: resp.headers["x-mode-key"] || "cash",
        mode_label: resp.headers["x-mode-label"] || "UPI",
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "QR generate nahi ho payi");
    }
  };

  const closeDeliver = () => {
    if (deliverModal?.qr) URL.revokeObjectURL(deliverModal.qr);
    setDeliverModal(null);
  };

  return (
    <div className={embedded ? "text-white" : "min-h-screen bg-slate-950 text-white"}>
      {!embedded && (
      <header className="sticky top-0 z-20 bg-slate-900/95 backdrop-blur border-b border-slate-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Truck size={22} className="text-orange-400" weight="fill" />
          <div>
            <h1 className="font-heading text-lg tracking-widest">DELIVERY</h1>
            <div className="text-[10px] font-mono-num text-slate-500">// {orders.length} orders ready to hand over</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="outline"
            className="rounded-sm h-9 border-slate-600 text-slate-300 hover:bg-slate-800"
            data-testid="delivery-refresh-btn">
            <ArrowClockwise size={14} />
          </Button>
          <div className="text-right text-xs mr-2 hidden sm:block">
            <div className="text-slate-300">{user?.name}</div>
            <div className="text-slate-500 font-mono-num">DELIVERY</div>
          </div>
          <button onClick={logout}
            className="h-9 w-9 rounded-sm border border-slate-700 hover:border-red-500 hover:bg-red-500/10 text-slate-400 hover:text-red-300 inline-flex items-center justify-center"
            data-testid="delivery-logout-btn">
            <SignOut size={16} />
          </button>
        </div>
      </header>
      )}
      {embedded && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-orange-400 text-xs font-mono-num tracking-widest">// DELIVERY</h2>
            <h1 className="font-heading text-3xl tracking-widest text-white flex items-center gap-2">
              <Truck size={26} weight="fill" className="text-orange-400" /> READY TO DISPATCH
            </h1>
            <div className="text-xs text-slate-500 font-mono-num mt-1">{orders.length} orders waiting to be handed over</div>
          </div>
          <Button onClick={load} variant="outline"
            className="rounded-sm h-9 border-slate-600 text-slate-300 hover:bg-slate-800"
            data-testid="delivery-refresh-btn">
            <ArrowClockwise size={14} className="mr-1" /> Refresh
          </Button>
        </div>
      )}

      <main className={embedded ? "space-y-3" : "p-4 max-w-5xl mx-auto space-y-3"}>
        {!embedded && <MyTasksBanner includeUnassigned />}
        {/* Tabs: Ready vs Today Delivered */}
        <div className="flex gap-2 border-b border-slate-800 pb-1 mb-2">
          <button
            onClick={() => setTab("ready")}
            className={"px-3 h-9 rounded-sm text-xs font-mono-num tracking-widest " +
              (tab === "ready" ? "bg-orange-500 text-white" : "text-slate-400 hover:bg-slate-800")}
            data-testid="tab-ready"
          >READY ({orders.length})</button>
          <button
            onClick={() => { setTab("today"); loadToday(); }}
            className={"px-3 h-9 rounded-sm text-xs font-mono-num tracking-widest " +
              (tab === "today" ? "bg-emerald-600 text-white" : "text-slate-400 hover:bg-slate-800")}
            data-testid="tab-today"
          >TODAY DELIVERED ({today.length})</button>
        </div>

        {tab === "ready" && loading && <div className="text-center text-slate-500 py-10 font-mono-num text-sm">Loading…</div>}
        {tab === "ready" && !loading && orders.length === 0 && (
          <div className="text-center py-16 text-slate-500">
            <Package size={48} className="mx-auto mb-3 opacity-40" />
            <div className="font-heading text-lg">Koi order delivery ke liye nahi hai</div>
          </div>
        )}
        {tab === "ready" && orders.map((o) => (
          <DeliveryCard
            key={o.id}
            order={o}
            onPreview={() => previewDesign(o)}
            onDeliver={() => openDeliver(o)}
          />
        ))}

        {tab === "today" && today.length === 0 && (
          <div className="text-center py-16 text-slate-500">
            <CheckCircle size={48} className="mx-auto mb-3 opacity-40" />
            <div className="font-heading text-lg">Aaj koi delivery nahi hui abhi tak</div>
          </div>
        )}
        {tab === "today" && today.map((o) => (
          <DeliveredCard key={o.id} order={o} />
        ))}
      </main>

      {/* Design preview modal */}
      <Dialog open={!!designPreview} onOpenChange={(v) => {
        if (!v && designPreview?.url) URL.revokeObjectURL(designPreview.url);
        if (!v) setDesignPreview(null);
      }}>
        <DialogContent
          className="max-w-[95vw] sm:max-w-4xl bg-slate-900 border-slate-700 p-0 gap-0"
          data-testid="design-preview-dialog"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800/60">
            <DialogTitle className="text-white font-heading tracking-widest text-base">DESIGN PREVIEW</DialogTitle>
            <button
              type="button"
              onClick={() => {
                if (designPreview?.url) URL.revokeObjectURL(designPreview.url);
                setDesignPreview(null);
              }}
              className="h-10 w-10 rounded-sm border border-slate-600 hover:border-red-500 hover:bg-red-500/10 text-slate-300 hover:text-red-300 inline-flex items-center justify-center text-2xl font-bold leading-none transition-colors"
              aria-label="Close preview"
              data-testid="design-preview-close-btn"
            >✕</button>
          </div>
          {designPreview && (
            <div className="max-h-[80vh] overflow-auto p-3 bg-slate-950">
              <img src={designPreview.url} alt={designPreview.filename}
                className="w-full h-auto rounded-sm border border-slate-700 mx-auto"
                data-testid="design-preview-image" />
              <div className="text-center text-[10px] text-slate-500 font-mono-num mt-2">{designPreview.filename}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Deliver-with-QR modal */}
      <DeliverDialog data={deliverModal} onClose={closeDeliver} onDone={(orderNum) => {
        closeDeliver();
        setSuccessOrder(orderNum);
        load(); loadToday();
        // Auto-dismiss the success animation after 2s.
        setTimeout(() => setSuccessOrder(null), 2200);
      }} />

      {/* Payment done animation overlay */}
      {successOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in"
             data-testid="payment-done-overlay">
          <div className="bg-slate-900 border-2 border-emerald-500 rounded-lg p-8 shadow-2xl text-center animate-in zoom-in duration-300">
            <div className="mx-auto w-24 h-24 rounded-full bg-emerald-500/20 border-4 border-emerald-500 flex items-center justify-center animate-pulse">
              <CheckCircle size={64} weight="fill" className="text-emerald-400" />
            </div>
            <div className="mt-4 text-emerald-300 font-heading text-2xl tracking-widest">PAYMENT DONE ✓</div>
            <div className="mt-1 text-white font-mono-num">#{successOrder}</div>
            <div className="mt-2 text-slate-400 text-xs">Order marked DELIVERED</div>
          </div>
        </div>
      )}
    </div>
  );
}


// Card variant for the "TODAY DELIVERED" tab — read-only summary.
function DeliveredCard({ order }) {
  return (
    <div className="bg-slate-900 border border-emerald-500/40 rounded-sm p-3 flex items-center justify-between gap-3"
         data-testid={`delivered-card-${order.order_number}`}>
      <div>
        <div className="flex items-center gap-2">
          <CheckCircle size={16} weight="fill" className="text-emerald-400 shrink-0" />
          <div className="text-orange-400 font-heading tracking-widest">#{order.order_number}</div>
        </div>
        <div className="text-white text-sm mt-0.5">{order.customer_name}</div>
        <div className="text-[10px] font-mono-num text-slate-500">
          {order.delivered_at ? new Date(order.delivered_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : ""}
          {order.delivered_by_name && ` · by ${order.delivered_by_name}`}
        </div>
      </div>
      <div className="text-right">
        <div className="text-emerald-300 font-heading font-mono-num text-lg">
          ₹{Number(order.total_amount || 0).toLocaleString("en-IN")}
        </div>
        <div className="text-[10px] font-mono-num text-slate-500">TOTAL</div>
      </div>
    </div>
  );
}


function DeliveryCard({ order, onPreview, onDeliver }) {
  const total = Number(order.total_amount || 0);
  const paid = Number(order.advance_paid || 0);
  const due = Math.max(total - paid - Number(order.discount || 0), 0);
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-sm p-4 space-y-3"
         data-testid={`delivery-card-${order.order_number}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-orange-400 font-heading text-xl tracking-widest">#{order.order_number}</div>
          <div className="text-white text-base mt-0.5">{order.customer_name}</div>
          {order.mobile && (
            <a href={`tel:${order.mobile}`}
              className="text-cyan-300 text-sm flex items-center gap-1 mt-0.5 font-mono-num hover:text-cyan-200"
              data-testid={`delivery-mobile-${order.order_number}`}>
              <Phone size={12} /> {order.mobile}
            </a>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono-num text-slate-500">TOTAL</div>
          <div className="text-lg font-heading font-mono-num">{fmt(total)}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="rounded-sm border border-slate-700 p-2">
          <div className="text-[10px] font-mono-num text-slate-500 tracking-widest">PAID</div>
          <div className="font-mono-num text-emerald-400" data-testid={`paid-amount-${order.order_number}`}>{fmt(paid)}</div>
        </div>
        <div className={"rounded-sm border p-2 " + (due > 0 ? "border-orange-500/50 bg-orange-500/5" : "border-emerald-500/50 bg-emerald-500/5")}>
          <div className="text-[10px] font-mono-num text-slate-500 tracking-widest">DUE</div>
          <div className={"font-mono-num " + (due > 0 ? "text-orange-400" : "text-emerald-400")}
               data-testid={`due-amount-${order.order_number}`}>
            {fmt(due)}
          </div>
        </div>
      </div>

      {order.address && (
        <div className="text-xs text-slate-400 border-l-2 border-slate-700 pl-2 line-clamp-2">
          {order.address}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Button onClick={onPreview} variant="outline"
          className="rounded-sm h-11 border-cyan-500/60 text-cyan-300 hover:bg-cyan-500/10"
          data-testid={`preview-design-btn-${order.order_number}`}>
          <Eye size={16} className="mr-1" /> DESIGN
        </Button>
        <Button onClick={onDeliver}
          className="rounded-sm h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-heading tracking-widest"
          data-testid={`deliver-btn-${order.order_number}`}>
          <CheckCircle size={16} className="mr-1" /> DELIVER
        </Button>
      </div>
    </div>
  );
}


/**
 * DeliverDialog — shown when balance > 0. Renders QR pre-locked to the due
 * amount + a big TAKE PHOTO button below. Once the delivery boy captures the
 * payment screenshot, uploads and marks the order delivered in a single
 * atomic flow. Amount + mode are taken from the QR headers so the boy never
 * touches a numeric input on the road.
 */
function DeliverDialog({ data, onClose, onDone }) {
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const cameraRef = useRef(null);

  useEffect(() => {
    // reset when opened / closed
    if (!data) {
      if (proofPreview) URL.revokeObjectURL(proofPreview);
      setProofFile(null);
      setProofPreview("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.order?.id]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (proofPreview) URL.revokeObjectURL(proofPreview);
    setProofFile(f);
    setProofPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!data) return;
    if (!proofFile) { toast.error("Payment ka photo lena zaroori hai"); return; }
    setBusy(true);
    try {
      // 1. Upload proof
      const fd = new FormData();
      fd.append("file", proofFile);
      const up = await api.post(`/delivery/${data.order.id}/upload-proof`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // 2. Complete delivery with QR's amount + mode (no manual entry).
      await api.post(`/delivery/${data.order.id}/complete`, {
        amount: data.amount,
        mode: data.mode_key || "cash",
        note: `UPI to ${data.upi_id}`,
        proof_upload_id: up.data?.id,
      });
      toast.success(`#${data.order.order_number} DELIVERED ✓`);
      onDone?.(data.order.order_number);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delivery failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!data} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-md bg-slate-900 border-slate-700 p-0 gap-0"
        data-testid="deliver-dialog"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800/60">
          <DialogTitle className="text-white font-heading tracking-widest text-base flex items-center gap-2">
            <QrCode size={18} className="text-orange-400" weight="fill" /> SCAN TO PAY
          </DialogTitle>
          <button
            type="button"
            onClick={onClose}
            className="h-10 w-10 rounded-sm border border-slate-600 hover:border-red-500 hover:bg-red-500/10 text-slate-300 hover:text-red-300 inline-flex items-center justify-center text-2xl font-bold leading-none"
            aria-label="Close"
            data-testid="deliver-close-btn"
          >✕</button>
        </div>
        {data && (
          <div className="p-4 space-y-4">
            <div className="text-center">
              <div className="text-[10px] font-mono-num text-slate-500 tracking-widest">
                #{data.order.order_number} · {data.order.customer_name}
              </div>
              <div className="text-emerald-300 text-4xl font-heading mt-1" data-testid="deliver-amount">{fmt(data.amount)}</div>
              <div className="text-slate-400 text-xs mt-0.5">{data.holder}</div>
              <div className="text-orange-300 font-mono-num text-xs">{data.upi_id}</div>
            </div>
            <img
              src={data.qr}
              alt="UPI QR"
              className="w-full h-auto rounded-sm bg-white p-2"
              data-testid="deliver-qr-image"
            />

            <input ref={cameraRef} type="file" accept="image/*" capture="environment"
              onChange={onFile} className="hidden" data-testid="deliver-proof-input" />

            {!proofPreview ? (
              <Button
                type="button"
                onClick={() => cameraRef.current?.click()}
                className="w-full rounded-sm h-14 bg-cyan-600 hover:bg-cyan-500 text-white font-heading tracking-widest text-base"
                data-testid="deliver-take-photo-btn"
              >
                <Camera size={20} className="mr-2" /> TAKE PHOTO OF PAYMENT
              </Button>
            ) : (
              <>
                <img
                  src={proofPreview}
                  alt="Payment proof"
                  className="w-full max-h-52 object-contain rounded-sm border border-slate-700"
                  data-testid="deliver-proof-preview"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => cameraRef.current?.click()}
                    className="rounded-sm h-11 border-slate-600 text-slate-300 hover:bg-slate-800"
                    data-testid="deliver-retake-btn"
                  >
                    <Camera size={16} className="mr-1" /> RETAKE
                  </Button>
                  <Button
                    onClick={submit}
                    disabled={busy}
                    className="rounded-sm h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-heading tracking-widest"
                    data-testid="deliver-confirm-btn"
                  >
                    <CheckCircle size={16} className="mr-1" /> {busy ? "SAVING…" : "CONFIRM"}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
